/**
 * Voucher codes, as the browser sees them.
 *
 * MIRRORS `game/utils/voucherCode.js`. The client cannot import `game/utils`
 * (no module under public/scripts does), so the normalization the server is
 * authoritative for is repeated here for one purpose only: telling the player
 * "that is not a code" before a round trip, and keeping what they type legible.
 * `tests/unit/ui/voucherCode.test.js` pins the two implementations together.
 */

export const VOUCHER_CODE_MIN = 4;
export const VOUCHER_CODE_MAX = 24;
export const GENERATED_CODE_LENGTH = 12;

const CONFUSABLES = { O: '0', I: '1', L: '1' };

/** The server's lookup form: uppercase, alphanumerics only, O/I/L folded. */
export function normalizeVoucherCode(raw) {
  if (typeof raw !== 'string') return '';
  const folded = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/[OIL]/g, (char) => CONFUSABLES[char]);
  // Over-long input is no code at all, exactly as the server answers it.
  return folded.length > VOUCHER_CODE_MAX ? '' : folded;
}

/**
 * What the input shows while the player types. Their spelling survives —
 * dashes they typed stay, the confusable fold is NOT applied here — and a
 * complete generated code with no dashes is grouped in fours so it reads the
 * way it was sent.
 */
export function formatVoucherCodeForDisplay(raw) {
  if (typeof raw !== 'string') return '';
  const kept = raw.toUpperCase().replace(/[^A-Z0-9-]/g, '').replace(/-{2,}/g, '-').slice(0, VOUCHER_CODE_MAX + 5);
  const bare = kept.replace(/-/g, '');
  if (!kept.includes('-') && bare.length === GENERATED_CODE_LENGTH) {
    return bare.match(/.{1,4}/g).join('-');
  }
  return kept;
}

export function isPlausibleVoucherCode(raw) {
  const length = normalizeVoucherCode(raw).length;
  return length >= VOUCHER_CODE_MIN && length <= VOUCHER_CODE_MAX;
}

/** The printed serial on the arrival ticket: the last four characters. */
export function voucherCodeHint(raw) {
  return normalizeVoucherCode(raw).slice(-4);
}
